import { useCallback, useEffect, useRef } from 'react';

import { Glyph, ICON } from './icons';

const FOCUSABLE =
  'a[href], button:not(:disabled), input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex]:not([tabindex="-1"])';

/**
 * Off-canvas shell navigation on the mobile shell. The hamburger is the only
 * opener; Escape, the scrim and navigation close it.
 */
export function MobileDrawer({
  open,
  enabled,
  onClose,
  returnFocusRef,
  label,
  closeLabel,
  children,
}: {
  open: boolean;
  enabled: boolean;
  onClose: () => void;
  returnFocusRef: React.RefObject<HTMLButtonElement | null>;
  label: string;
  closeLabel: string;
  children: React.ReactNode;
}): React.ReactElement {
  const panelRef = useRef<HTMLDivElement>(null);
  const lastFocused = useRef<HTMLElement | null>(null);
  const onCloseRef = useRef(onClose);

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  const close = useCallback((): void => {
    onCloseRef.current();
  }, []);

  useEffect(() => {
    if (!enabled || !open) return;
    lastFocused.current = document.activeElement as HTMLElement | null;
    const panel = panelRef.current;
    if (!panel) return;
    const first = panel.querySelector<HTMLElement>(FOCUSABLE);
    (first ?? panel).focus();

    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        if (panel.querySelector('[data-state="open"]')) return;
        event.preventDefault();
        close();
        return;
      }
      if (event.key !== 'Tab') return;
      const stops = [...panel.querySelectorAll<HTMLElement>(FOCUSABLE)].filter(
        (element) => element.getAttribute('aria-hidden') !== 'true',
      );
      if (stops.length === 0) return;
      const firstStop = stops[0];
      const lastStop = stops[stops.length - 1];
      if (!firstStop || !lastStop) return;
      if (event.shiftKey && document.activeElement === firstStop) {
        event.preventDefault();
        lastStop.focus();
      } else if (!event.shiftKey && document.activeElement === lastStop) {
        event.preventDefault();
        firstStop.focus();
      }
    };

    panel.addEventListener('keydown', onKey, true);
    return () => {
      panel.removeEventListener('keydown', onKey, true);
      const target = returnFocusRef.current ?? lastFocused.current;
      target?.focus();
    };
  }, [enabled, open, close, returnFocusRef]);

  if (!enabled) return <>{children}</>;

  return (
    <>
      <button
        type="button"
        className={open ? 'drawer__scrim is-open' : 'drawer__scrim'}
        aria-label={label}
        tabIndex={-1}
        onClick={close}
      />
      <div
        ref={panelRef}
        id="mobile-navigation"
        className={open ? 'drawer is-open' : 'drawer'}
        role="dialog"
        aria-modal={open || undefined}
        aria-label={label}
        aria-hidden={open ? undefined : true}
        inert={!open}
        tabIndex={-1}
      >
        <button type="button" className="drawer__close nb" aria-label={closeLabel} onClick={close}>
          <Glyph d={ICON.close} size={20} />
        </button>
        {children}
      </div>
    </>
  );
}
